import { proxy } from 'valtio';

import { type ClientFeatureFlagName } from './client-feature-flags-state';

/**
 * When creating an experiment, we configure params for that experiment to drive
 * behaviour in code (rather than using hard-coded cohort names)
 *
 * With A/B testing, we can use a simple boolean param (`enabled`) to control
 * behaviour - note that this param has to be configured when you create a new
 * experiment in Statsig.
 *
 * See: https://docs.statsig.com/experiments-plus/create-new#configure-your-groups-and-parameters
 */
const localExperimentConfig = {
  /**
   * A/A test for validating our Statsig integration and our metrics instrumentation
   * https://switcheroo.atlassian.com/gates/5f23451e-6414-449b-a04c-e917a07fb929
   */
  whiteboardAATest: {
    experimentId: 'whiteboards-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Progressively disables awareness features in canvas proportionate to the number of collaborators/clients.
   * https://switcheroo.atlassian.com/ui/gates/4341b653-3f4c-4a5c-af21-7c6ca9cba2bd/key/cc-content-types-progressive-awareness-disablement
   */
  progressiveAwarenessDisablement: {
    experimentId: 'cc-content-types-progressive-awareness-disablement',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables more shapes
   * https://switcheroo.atlassian.com/ui/gates/0957c8e1-0427-4694-b3f7-39ba65a9132d/key/cc-whiteboards-more-shapes
   */
  moreShapes: {
    experimentId: 'cc-whiteboards-more-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables an overlay with onboarding action buttons on board creation
   * https://switcheroo.atlassian.com/ui/gates/394d0e3d-e742-4507-a7dd-aa5b8690039c/key/cc-whiteboards-user-onboarding
   */
  onboardingOverlay: {
    experimentId: 'cc-whiteboards-user-onboarding',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Allow users to create webgl rendered cloud icons
   * https://switcheroo.atlassian.com/ui/gates/ce8f9a41-4c01-4fa1-baa2-a661f56eda67/key/cc-whiteboards-cloud-shapes
   */
  cloudShapes: {
    experimentId: 'cc-whiteboards-cloud-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test against cloud shapes to verify numbers
   * https://switcheroo.atlassian.com/ui/gates/7b16fbb4-995f-4780-9a85-eb434957ca1d/key/cc-whiteboards-cloud-shapes-aa-test
   */
  cloudShapesAATest: {
    experimentId: 'cc-whiteboards-cloud-shapes-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new checkbox field type.
   * https://switcheroo.atlassian.com/ui/gates/8a80339d-1133-43e8-8498-996d107ed89f/key/databases-frontend-checkbox-field-type-experiment
   */
  checkboxFieldType: {
    experimentId: 'databases-frontend-checkbox-field-type-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the calendar layout for the databases.
   * https://switcheroo.atlassian.com/ui/gates/aca974aa-cdae-45d4-8967-d86d12de0d05/key/databases-frontend-layout-calendar-experiment
   */
  layoutCalendar: {
    experimentId: 'databases-frontend-layout-calendar-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables 2 way sync of Jira Work items from databases
   * https://switcheroo.atlassian.com/ui/gates/b5bd0131-2b49-4908-8eca-3c272cdc4dac/key/confluence_database_2_way_sync
   */
  databases2waySyncEnabled: {
    experimentId: 'confluence_database_2_way_sync',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/1ceff846-d891-440a-a6f3-3000293886f9/key/show-sidebar-smart-actions
   */
  showSidebarSmartActions: {
    experimentId: 'show-sidebar-smart-actions',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables date range and time for date field settings
   * https://switcheroo.atlassian.com/ui/gates/0fc6a623-4964-4856-b4f2-80b8f3beb1d9/key/databases-frontend-field-settings-date-experiment
   */
  dateFieldSettings: {
    experimentId: 'databases-frontend-field-settings-date-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables various improvements to the Jira Import experience, including moving it to a sidebar,
   * adding filtering and more. Related to NLP for Jira Import picker.
   * https://switcheroo.atlassian.com/ui/gates/61a8ecac-f332-4ed1-9175-a6dbc52cd6e5/key/confluence-whiteboards-improve-jira-search
   */
  shouldUseImprovedJiraImport: {
    experimentId: 'confluence-whiteboards-improve-jira-search',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables display size options for media field settings
   * https://switcheroo.atlassian.com/ui/gates/6ba5a334-54a8-47a6-ad07-c4d66248e271/key/databases-frontend-field-settings-media-experiment
   */
  mediaFieldSettings: {
    experimentId: 'databases-frontend-field-settings-media-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables new board grouping features (subgrouping, group sorting, grouping by non-tag fields).
   * https://switcheroo.atlassian.com/ui/gates/995573d3-e0ee-450c-9f3d-2abba6dfd9c1/key/databases-frontend-grouping-board-experiment
   */
  databasesBoardGrouping: {
    experimentId: 'databases-frontend-grouping-board-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables onboarding for the new board grouping features.
   * https://switcheroo.atlassian.com/ui/gates/49bf3493-86a9-4046-986c-348bd46907b0/key/databases-frontend-grouping-onboarding-experiment
   */
  databasesBoardGroupingOnboarding: {
    experimentId: 'databases-frontend-grouping-onboarding-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Improvements / fixes around working with Jira data in DBs.
   * https://switcheroo.atlassian.com/ui/gates/bb578d82-9575-44e8-8426-6d7f412de7f9/key/databases-frontend-jira-slice-and-dice
   */
  databasesJiraSliceAndDice: {
    experimentId: 'databases-frontend-jira-slice-and-dice',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables rich text editing in databases
   * https://switcheroo.atlassian.com/ui/gates/847258fa-bf8c-4b0e-b3ab-e72c4980a040/key/databases-rich-text-experiment
   */
  databasesRichText: {
    experimentId: 'databases-rich-text-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables NLP (Natural Language Processing) for Jira Import picker
   * https://switcheroo.atlassian.com/ui/gates/071f50a4-1f21-4371-9dc9-8dd6c3844a2b/key/confluence-whiteboards-use-nlp-jira-issue-search
   */
  shouldUseNlpForJiraIssueSearch: {
    experimentId: 'confluence-whiteboards-use-nlp-jira-issue-search',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new database more menu entry
   * https://switcheroo.atlassian.com/ui/gates/6f25555f-0127-4f61-9541-1a0661f69cff/key/cc-whiteboards-create-database-from-more-menu
   */
  databaseMoreMenuEntry: {
    experimentId: 'cc-whiteboards-create-database-from-more-menu',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Allows users to create databases with Rovo
   * https://switcheroo.atlassian.com/ui/gates/7b25cf1f-1537-4311-8813-0ca22cd99271/key/cc-aifc-databases-experiment
   */
  databasesAIFC: {
    experimentId: 'cc-aifc-databases-experiment',
    defaultParams: {
      enabled: true,
    },
  },
  /**
   /**
   * Enables the new diagramming agent using an ELK layout engine instead of dagre.
   * https://switcheroo.atlassian.com/ui/gates/aa407bad-b331-4d9d-983f-65dd4273f1c7/key/whiteboards-use-v2-diagramming-agent
   */
  v2DiagrammingAgent: {
    experimentId: 'whiteboards-use-v2-diagramming-agent',
    defaultParams: {
      enabled: false,
    },
  },
  /** Enables random colour option on sticky notes */
  randomStickyColour: {
    experimentId: 'random_sticky_colour',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new AI button in the mini modal
   * https://switcheroo.atlassian.com/ui/gates/225bbb2b-ee14-4242-b3af-74794a11b89b/key/rovo_chat_refresh_v1_experiment
   */
  miniModalAIButton: {
    experimentId: 'rovo_chat_refresh_v1_experiment',
    defaultParams: {
      enabled: false,
      isWhiteboardRovoButtonEnabled: false,
    },
  },
  /**
   * Enables whiteboard rotation renderer/system support.
   * https://switcheroo.atlassian.com/ui/gates/2b8d2315-1a14-4478-809a-ffa20e91eeef/key/whiteboard-rotation
   */
  whiteboardRotation: {
    experimentId: 'whiteboard-rotation',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables the Jira Unfurl button in the element toolbar
   * https://switcheroo.atlassian.com/ui/gates/b3c5f3a6-cfe1-4636-bcfd-2d6c58a1d24b/key/cc-whiteboards-jira-unfurl
   */
  whiteboardsJiraUnfurl: {
    experimentId: 'cc-whiteboards-jira-unfurl',
    defaultParams: {
      enabled: false,
    },
    prerequisiteFeatureFlags: ['jiraUnfurlModule'],
  },

  /**
   * Enables consistent comment editor placeholders as part of Project Cohiti https://product-fabric.atlassian.net/browse/CPC-72
   * https://switcheroo.atlassian.com/ui/gates/310e89e8-3189-41c5-bcfc-7898f28a3b7e/key/confluence_comments_cohiti_consistent_placeholders
   */
  standardizedCommentPlaceholder: {
    experimentId: 'confluence_comments_cohiti_consistent_placeholders',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Reopen the templates modal when the user performs an undo immediately after inserting a template
   * https://switcheroo.atlassian.com/ui/gates/8da14f9d-f875-4dab-800a-33a26e3606be/key/whiteboards-template-insertion-undo-behaviour
   */
  templateInsertionUndoBehaviour: {
    experimentId: 'whiteboards-template-insertion-undo-behaviour',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Allows you to select the quality of exporting a whiteboard
   * https://switcheroo.atlassian.com/ui/gates/fede86d6-e54e-4340-a218-cd3a4f6cd144/key/whiteboard_high_quality_export
   */
  whiteboardHighQualityExport: {
    experimentId: 'whiteboard_high_quality_export',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the magic pen feature
   * https://switcheroo.atlassian.com/ui/gates/dfdc361a-acfe-45df-9a6e-103048146be5/key/cc-whiteboards-magic-pen
   */
  magicPen: {
    experimentId: 'cc-whiteboards-magic-pen',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new diagramming arrowheads feature
   * https://switcheroo.atlassian.com/ui/gates/84ecd076-717f-47de-9092-d41ffc1e1d5f/key/cc-whiteboards-diagramming-arrowheads
   */
  diagrammingArrowheads: {
    experimentId: 'cc-whiteboards-diagramming-arrowheads',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * AA for the new diagramming arrowheads feature
   * https://switcheroo.atlassian.com/ui/gates/7ca5dd8e-f908-4e7a-b488-732a63c1663a/key/cc-whiteboards-december-25-aa-arrowheads
   */
  diagrammingArrowheadsAa: {
    experimentId: 'cc-whiteboards-december-25-aa-arrowheads',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * AA for the new diagramming arrowheads feature, removing `Confluence Prod Synthetics`
   * segment from overrides to re-include canvas-testing-bot
   * https://switcheroo.atlassian.com/ui/gates/57cc7749-6274-4069-bed9-7704eb762dd8/key/cc-whiteboards-december-25-aa-arh-no-overrides
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/cc-whiteboards-december-25-aa-arh-no-overrides/setup
   */
  diagrammingArrowheadsAaIncludingSynthetics: {
    experimentId: 'cc-whiteboards-december-25-aa-arh-no-overrides',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * AA for scoped arrowheads - fires exposure only when dropdown is open
   * https://switcheroo.atlassian.com/ui/gates/f0082cfd-2069-4466-8cc0-4eab8d9a97f6/key/cc-whiteboards-december-25-aa-scoped-arrowheads
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/cc-whiteboards-december-25-aa-scoped-arrowheads/setup
   */
  diagrammingArrowheadsAaScoped: {
    experimentId: 'cc-whiteboards-december-25-aa-scoped-arrowheads',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables quick object transformation (e.g. from sticky -> textbox)
   * https://switcheroo.atlassian.com/ui/gates/c095ca94-e548-4416-a275-302d49024d2c/key/whiteboards-quick-object-transform
   */
  quickTransform: {
    experimentId: 'whiteboards-quick-object-transform',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new UML shapes feature
   * https://switcheroo.atlassian.com/ui/gates/d3d9c1ad-cfec-4efc-8124-fc38e6822fc8/key/cc-whiteboards-uml-shapes
   */
  umlShapes: {
    experimentId: 'cc-whiteboards-uml-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test against equidistant spacing to verify numbers
   * https://switcheroo.atlassian.com/ui/gates/cea32b83-e7fb-4d4b-a17d-87abcdaf39c1/key/cc-whiteboards-equidistant-spacing-aa-test
   */
  equidistantSpacingAATest: {
    experimentId: 'cc-whiteboards-equidistant-spacing-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test for December 2025 for ALL whiteboards loads AND confluence_web cross-app targeting
   * https://switcheroo.atlassian.com/ui/gates/dea32703-7e7e-45f9-8e7f-10a1aff762c9/key/cc-whiteboards-december-25-aa-tenant
   */
  december25AaTenant: {
    experimentId: 'cc-whiteboards-december-25-aa-tenant',
    defaultParams: {
      enabled: false,
      isEnabled: false,
    },
  },
  /**
   * A/A test for December 2025 for atlassianAccountId
   * https://switcheroo.atlassian.com/ui/gates/87c2fe7a-dd6c-48e0-bd04-1f112fe11925/key/cc-whiteboards-december-25-aa-atlassianaccountid
   */
  december25AaAtlassianAccountId: {
    experimentId: 'cc-whiteboards-december-25-aa-atlassianaccountid',
    defaultParams: {
      enabled: false,
      isEnabled: false,
    },
  },

  /**
   * A/A test for October 25th for ALL whiteboards loads - ignores the existence of the
   * holdout, i.e. absolutely 100% of exposures
   * https://switcheroo.atlassian.com/ui/gates/110c9054-7e7b-4f17-a885-d1a6aca27e5d/key/cc-whiteboards-october-25-aa-all
   */
  october25AaAll: {
    experimentId: 'cc-whiteboards-october-25-aa-all',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * A/A test for October 25th for whiteboards loads in holdout - WITH the
   * product-cc-tenant_id-fy26h1-tenant-user-gate mismatch - gate target
   * https://switcheroo.atlassian.com/ui/gates/795fa67c-9c44-4eb4-a149-b6f03907e3ab/key/cc-whiteboards-october-25-aa-all-holdout
   */
  october25AaAllHoldout: {
    experimentId: 'cc-whiteboards-october-25-aa-all-holdout',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test for October 25th for Jira import loads - WITH holdout
   * https://switcheroo.atlassian.com/ui/gates/02f2c458-eae9-4a40-91ba-2beb2cf24416/key/cc-whiteboards-october-25-aa-jira-import
   */
  october25AaJiraImport: {
    experimentId: 'cc-whiteboards-october-25-aa-jira-import',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * UX health week Dec 2025!!
   * Batch all your UX health week changes here.
   *
   * tenantId based targeting.
   *
   * https://switcheroo.atlassian.com/ui/gates/d5f852b1-9ec4-4c0b-989b-edb64e252277/key/cc-whiteboards-ux-health-dec-2025
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/cc-whiteboards-ux-health-dec-2025/results
   */
  uxHealthDec2025: {
    experimentId: 'cc-whiteboards-ux-health-dec-2025',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Adds the ability to attach multiple line labels to a path
   * https://switcheroo.atlassian.com/ui/gates/a583606d-e37d-4754-b3bf-5fc3f0b8d762/key/whiteboards-multi-path-labels
   */
  multiPathLabels: {
    experimentId: 'whiteboards-multi-path-labels',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables equidistant spacing and snapping when dragging elements near other elements
   * Shows guide lines when elements are equidistant
   * https://switcheroo.atlassian.com/ui/gates/c246eae7-8634-42ec-9cd9-a5a724c48c54/key/cc-whiteboards-equidistant-spacing
   */
  equidistantSpacing: {
    experimentId: 'cc-whiteboards-equidistant-spacing',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables contextual UI side effects when specific templates are inserted
   * https://switcheroo.atlassian.com/ui/gates/73b44d17-863b-4a64-b64b-bd2f5cc604e4/key/whiteboards-template-insertion-contextual-shapes
   */
  whiteboardsTemplateInsertionContextualShapes: {
    experimentId: 'whiteboards-template-insertion-contextual-shapes',
    defaultParams: {
      variant: 'control' as 'control' | 'moreShapesMenu' | 'shapesMenu',
    },
  },
  /**
   * Enforces a maximum limit of 100 operations on the undo/redo stack to prevent memory issues
   */
  undoRedoStackLimit: {
    experimentId: 'whiteboards_undo_redo_stack_limit',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Controls confluence race mode (the racing minigame)
   * https://switcheroo.atlassian.com/ui/gates/ba1190fc-0902-4a27-beac-73348b01f108/key/confluence_race_mode
   */
  confluenceRaceMode: {
    experimentId: 'confluence_race_mode',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables the preview panel for whiteboards
   * https://switcheroo.atlassian.com/ui/gates/c05f0d13-ade7-4ab8-9f28-422609eba6c5/key/preview_panel_for_whiteboards
   */
  whiteboardsPreviewPanel: {
    experimentId: 'preview_panel_for_whiteboards',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables the preview panel for databases
   * https://switcheroo.atlassian.com/ui/gates/bf435ee6-e476-495a-99c4-f6e95672e5a8/key/preview_panel_for_databases
   */
  databasesPreviewPanel: {
    experimentId: 'preview_panel_for_databases',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables the new grouping features
   * https://switcheroo.atlassian.com/ui/gates/1ab3aba9-f933-4ef1-b75b-cbfe6cb2bab7/key/whiteboards-grouping
   */
  grouping: {
    experimentId: 'whiteboards-grouping',
    defaultParams: {
      enabled: true,
    },
  },
  /**
   * Enables improved copy paste improvements
   * https://switcheroo.atlassian.com/ui/gates/6993e479-3728-4ad4-8982-92d451142225/key/cc-whiteboards-improved-copy-paste
   */
  improvedCopyPasteGate: {
    experimentId: 'cc-whiteboards-improved-copy-paste',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enabled "add text" button on paths when selected
   * https://switcheroo.atlassian.com/ui/gates/59c9b8b3-d1a0-4dd3-9771-e5b2c39679e1/key/cc-whiteboards-create-path-label-button
   */
  createPathLabelButton: {
    experimentId: 'cc-whiteboards-create-path-label-button',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables whiteboards should use multiple fields in smart sections.
   * https://hello.atlassian.net/wiki/spaces/CAN/pages/5978422432/Feature+Smart+Sections+Multiple+Fields
   * https://switcheroo.atlassian.com/ui/gates/1999347d-9d6a-4192-a1a6-8b4c2c97e995/key/whiteboards-use-multiple-fields-smart-sections
   */
  smartSectionsMultipleFields: {
    experimentId: 'whiteboards-use-multiple-fields-smart-sections',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * a test experiment for dev against switcheroo staging
   * https://switcheroo.stg.atlassian.com/ui/gates/974634cb-777c-424e-887b-126325fee1c3/key/who_canvas_test_experiment_2
   */
  devTestExperiment: {
    experimentId: 'who_canvas_test_experiment',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * Disables the redux cell selections slice and enables the new selections architecture.
   */
  databasesSelectionsPerformance: {
    experimentId: 'ctd-5604-databases-selections-performance',
    defaultParams: {
      isEnabled: false,
      cohort: 'control', // 'control' or 'test'
    },
  },

  /**
   * Enables "make a copy" CTA over "duplicate" CTA on the copy content strings
   * https://switcheroo.atlassian.com/ui/gates/dc6ec7b0-44a3-441a-b28b-ae3e39181ee8/key/make_a_copy_cta
   */
  makeACopyCta: {
    experimentId: 'make_a_copy_cta',
    defaultParams: {
      enabled: false, // unused, cohort is used instead
      cohort: 'control', // 'control' or 'test'
    },
  },

  /**
   * Create with Rovo blank object experience for databases.
   * Shows the blank object experience modal on new database creation.
   * https://switcheroo.atlassian.com/ui/gates/cwr_blank_object_experiment
   */
  cwrBlankObjectExperiment: {
    experimentId: 'cwr_blank_object_experiment',
    defaultParams: {
      cohort: 'control' as 'control' | 'variant1' | 'variant2' | 'variant3',
      isEnabled: false,
    },
  },

  /**
   * Moves the create Jira work items feature from the sidebar panel to the drawer
   * https://switcheroo.atlassian.com/ui/gates/3bf1171a-072d-403d-9ba2-d507032f15b6/key/cc-whiteboards-create-jira-items-drawer
   */
  createJiraWorkItemsDrawer: {
    experimentId: 'cc-whiteboards-create-jira-items-drawer',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enabled new AI Diagram mode, specifically flow charts and mind maps
   * https://switcheroo.atlassian.com/ui/gates/79b3d0ef-bbbd-495b-865f-3fcfe60672b2/key/cc-maui-experiment
   */
  mauiExperiment: {
    experimentId: 'cc-maui-experiment',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables AI diagram mode M2, specifically cloud architecture diagrams
   */
  diagrammingAgentM2: {
    experimentId: 'cc-whiteboards-diagramming-agent-m2',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables native whiteboard embeds
   * https://switcheroo.atlassian.com/ui/gates/23c38d46-0999-48b0-b203-0647fbcac9be/key/native_whiteboard_embed
   */
  nativeEmbeds: {
    experimentId: 'native_whiteboard_embed',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables MUI Slides
   * https://switcheroo.atlassian.com/ui/gates/0bbf3d23-a3b9-496a-b7a0-0f046ee7a58b/key/cc-mui-slides-experiment
   */
  muiSlides: {
    experimentId: 'cc-mui-slides-experiment',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables table/grid elements in Whiteboards for structured data organization
   * https://switcheroo.atlassian.com/ui/gates/c97791d7-232f-4054-a498-6be5d4c0223e/key/cc-whiteboards-tables
   */
  whiteboardsTablesExperiment: {
    experimentId: 'cc-whiteboards-tables',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * Enables the font size uplift toolbar split between text level and font size.
   * https://switcheroo.atlassian.com/ui/gates/2911877c-b06d-4e68-80a0-3c1615e85bfd/key/cc-whiteboards-font-sizing-uplift
   */
  fontSizingUplift: {
    experimentId: 'cc-whiteboards-font-sizing-uplift',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * Enables new contextual menus for database view settings (toolbar and view menus).
   * https://switcheroo.atlassian.com/ui/gates/e2c1638f-90d9-4e98-8049-50d0e42929d9/key/databases-frontend-view-contextual-menus
   */
  databasesViewContextualMenus: {
    experimentId: 'databases-frontend-view-contextual-menus',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * Native DB Embeds V2: Vertical resize with snap-to-row, entry embed redesign.
   * https://switcheroo.atlassian.com/ui/gates/60bfd60b-c2c6-496f-adab-ccb2b8c0c2b1/key/databases-native-embeds-v2
   */
  dbNativeEmbedsV2: {
    experimentId: 'databases-native-embeds-v2',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * SPARK: Shows sticky authors as everyone who has contributed to the content rather than just the creator
   * https://switcheroo.atlassian.com/ui/gates/3abe7966-7ede-4416-9c22-a14b1036cc74/key/spark_whiteboards_multiple_sticky_authors
   */
  multipleStickyAuthors: {
    experimentId: 'spark_whiteboards_multiple_sticky_authors',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * A/A test for JPD whiteboards integration
   * https://switcheroo.atlassian.com/ui/gates/0a8f2c1f-66b7-4260-9dcf-d5b66e260c7f/key/jpdgrw-147_whiteboards_to_jpd_aa_test
   */
  jpdWhiteboardsAATest: {
    experimentId: 'jpdgrw-147_whiteboards_to_jpd_aa_test',
    defaultParams: {
      isEnabled: false,
    },
  },

  /**
   * A/B test for JPD whiteboards integration
   * https://switcheroo.atlassian.com/ui/gates/2173a603-fc19-4cae-a9f1-f84237a2d337/key/jpd-recommendation-in-whiteboard-pol-22311
   */
  jpdWhiteboardsABTest: {
    experimentId: 'jpd-recommendation-in-whiteboard-pol-22311',
    defaultParams: {
      isEnabled: false,
    },
  },
} satisfies Record<string, Experiment>;

type Experiment = {
  experimentId: string;
  defaultParams: Record<string, any>;
  prerequisiteFeatureFlags?: ClientFeatureFlagName[];
};
type ExperimentKey = keyof typeof localExperimentConfig;
export const experimentConfig: Record<ExperimentKey, Experiment> = localExperimentConfig;

type ExperimentConfig = typeof experimentConfig;
export type ExperimentName = keyof ExperimentConfig;
export type ExperimentParams = {
  [key in keyof ExperimentConfig]: ExperimentConfig[key]['defaultParams'];
};

export type ExperimentOverrides = {
  [key in keyof ExperimentConfig]?: Partial<ExperimentConfig[key]['defaultParams']>;
};

export const experimentOverrides = proxy<ExperimentOverrides>({});

export function isExperimentName(value: string): value is ExperimentName {
  return value in experimentConfig;
}
